import { createTheme } from '@mui/material/styles'
import type { Shadows } from '@mui/material/styles'

// Design tokens from the design guide
const designTokens = {
  colors: {
    primary: {
      brown: '#8B4513',
      gold: '#DAA520',
      cream: '#F5F5DC',
      dark: '#2F1B14'
    },
    secondary: {
      humidorGreen: '#228B22',
      ratingGold: '#FFD700',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336',
      info: '#2196F3'
    },
    neutral: {
      white: '#FFFFFF',
      lightGray: '#F8F9FA',
      mediumGray: '#6C757D',
      darkGray: '#343A40',
      black: '#000000'
    }
  },
  typography: {
    fontFamily: {
      primary: 'Inter, sans-serif',
      display: 'Playfair Display, serif'
    },
    fontSize: {
      h1: '2.5rem',
      h2: '2rem',
      h3: '1.5rem',
      h4: '1.25rem',
      h5: '1.125rem',
      h6: '1rem',
      bodyLarge: '1.125rem',
      body: '1rem',
      bodySmall: '0.875rem',
      caption: '0.75rem'
    }
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: designTokens.colors.primary.brown,
      light: '#A0522D',
      dark: '#654321',
      contrastText: designTokens.colors.neutral.white
    },
    secondary: {
      main: designTokens.colors.primary.gold,
      light: '#FFD700',
      dark: '#B8860B',
      contrastText: designTokens.colors.neutral.black
    },
    success: {
      main: designTokens.colors.secondary.success,
      light: '#81C784',
      dark: '#388E3C'
    },
    warning: {
      main: designTokens.colors.secondary.warning,
      light: '#FFB74D',
      dark: '#F57C00'
    },
    error: {
      main: designTokens.colors.secondary.error,
      light: '#E57373',
      dark: '#D32F2F'
    },
    info: {
      main: designTokens.colors.secondary.info,
      light: '#64B5F6',
      dark: '#1976D2'
    },
    background: {
      default: designTokens.colors.primary.cream,
      paper: designTokens.colors.neutral.white
    },
    text: {
      primary: designTokens.colors.primary.dark,
      secondary: designTokens.colors.neutral.mediumGray
    }
  },
  typography: {
    fontFamily: designTokens.typography.fontFamily.primary,
    h1: {
      fontFamily: designTokens.typography.fontFamily.display,
      fontSize: designTokens.typography.fontSize.h1,
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.025em'
    },
    h2: {
      fontFamily: designTokens.typography.fontFamily.display,
      fontSize: designTokens.typography.fontSize.h2,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em'
    },
    h3: {
      fontFamily: designTokens.typography.fontFamily.display,
      fontSize: designTokens.typography.fontSize.h3,
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.025em'
    },
    h4: {
      fontFamily: designTokens.typography.fontFamily.primary,
      fontSize: designTokens.typography.fontSize.h4,
      fontWeight: 600,
      lineHeight: 1.4
    },
    h5: {
      fontFamily: designTokens.typography.fontFamily.primary,
      fontSize: designTokens.typography.fontSize.h5,
      fontWeight: 600,
      lineHeight: 1.5
    },
    h6: {
      fontFamily: designTokens.typography.fontFamily.primary,
      fontSize: designTokens.typography.fontSize.h6,
      fontWeight: 600,
      lineHeight: 1.5
    },
    body1: {
      fontSize: designTokens.typography.fontSize.body,
      lineHeight: 1.6
    },
    body2: {
      fontSize: designTokens.typography.fontSize.bodySmall,
      lineHeight: 1.6
    },
    button: {
      fontWeight: 600,
      textTransform: 'none'
    }
  },
  shape: {
    borderRadius: 8
  },
  shadows: [
    'none',
    designTokens.shadows.sm,
    designTokens.shadows.md,
    designTokens.shadows.lg,
    '0 20px 25px rgba(0, 0, 0, 0.1)',
    '0 25px 50px rgba(0, 0, 0, 0.1)',
    '0 10px 15px rgba(0, 0, 0, 0.13)',
    '0 12px 18px rgba(0, 0, 0, 0.14)',
    '0 14px 21px rgba(0, 0, 0, 0.15)',
    '0 16px 24px rgba(0, 0, 0, 0.16)',
    '0 18px 27px rgba(0, 0, 0, 0.17)',
    '0 20px 30px rgba(0, 0, 0, 0.18)',
    '0 22px 33px rgba(0, 0, 0, 0.19)',
    '0 24px 36px rgba(0, 0, 0, 0.20)',
    '0 26px 39px rgba(0, 0, 0, 0.21)',
    '0 28px 42px rgba(0, 0, 0, 0.22)',
    '0 30px 45px rgba(0, 0, 0, 0.23)',
    '0 32px 48px rgba(0, 0, 0, 0.24)',
    '0 34px 51px rgba(0, 0, 0, 0.25)',
    '0 36px 54px rgba(0, 0, 0, 0.26)',
    '0 38px 57px rgba(0, 0, 0, 0.27)',
    '0 40px 60px rgba(0, 0, 0, 0.28)',
    '0 42px 63px rgba(0, 0, 0, 0.29)',
    '0 44px 66px rgba(0, 0, 0, 0.30)',
    '0 46px 69px rgba(0, 0, 0, 0.31)'
  ] as Shadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.md,
          padding: '12px 24px',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: designTokens.shadows.md
          }
        },
        contained: {
          boxShadow: designTokens.shadows.sm,
          '&:hover': {
            boxShadow: designTokens.shadows.md
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          boxShadow: designTokens.shadows.sm,
          border: '1px solid #E5E7EB',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: designTokens.shadows.md,
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.md,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: designTokens.colors.primary.brown
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: designTokens.colors.primary.brown,
              borderWidth: 2
            }
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: designTokens.colors.neutral.white,
          color: designTokens.colors.primary.dark,
          boxShadow: designTokens.shadows.sm,
          borderBottom: '1px solid #E5E7EB'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: designTokens.colors.neutral.white,
          borderRight: '1px solid #E5E7EB'
        }
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          minWidth: 0,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          minWidth: 0,
        },
      },
    },
  }
})

export default theme
